import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InventoryItem,
  InventoryGroup,
  ProductTemplateType,
  ProductGroupType,
  SearchedProduct,
  ProductModelSearchResult,
  ManufacturerSearchResult
} from 'core/types';
export interface ProductGroup {
  id: number;
  group: string;
  count: number;
  names?: string[];
}

export interface InventoryState {
  loading: boolean;
  // Scoped to `requestProductGroup`/`resetProductGroup` (a single group's
  // product list, `/inventory/group/:id`) — split off from the shared
  // `loading` flag so a failure/reset here can't stall the unrelated
  // `/inventory` landing skeleton, which reused the same boolean purely by
  // historical accident, not because the two are actually related. Consumed
  // by InventoryProducts.tsx and InventoryHomeSelections.tsx, both of which
  // call `requestProductGroup` for their own group's list.
  productGroupLoading: boolean;
  productDetailsLoading: boolean;
  error: string | null;
  productGroups: ProductGroup[];
  products: InventoryItem[];
  product_count: number | null;
  inventory: InventoryGroup[];
  product_id: number | null;
  product: InventoryItem | null;
  productTypeGroupsList: ProductGroupType[];
  productTemplate: ProductTemplateType | null;
  searchedProducts: SearchedProduct[];
  addItemModalVisible: boolean;
  // Drives the `.hk-digital-inventory-add-product-modal` desktop --height
  // toggle (theme/variables.scss: 496px landing vs 740px manual form) — read
  // by InventoryGroups.tsx/InventoryProducts.tsx, which own the IonModal
  // element that InventoryAddProduct.tsx (where the landing/manual step
  // itself lives) does not.
  addItemShowLanding: boolean;
  itemDetailsModalVisible: boolean;
  selectionDetailsModalVisible: boolean;
  selectionDetailsType: 'option' | 'color' | null;
  updateProductLoading: boolean;
  deleteProductLoading: boolean;
  productModels: ProductModelSearchResult[];
  productModelsLoading: boolean;
  manufacturers: ManufacturerSearchResult[];
  manufacturersLoading: boolean;
  // Dedicated result slots for `resolveAnalyzedProductUpdate`'s one-shot OCR
  // lookups, deliberately NOT the `productModels`/`manufacturers` fields above.
  // Those two are the live backing lists for the cascading Brand/Model selects
  // (ProductModelSearchSelect, useProductCatalogEditFields) — an OCR run
  // searching for a single label string narrows them to that one term's
  // matches, and because the edit hook only refetches manufacturers when
  // `typeId` *changes*, re-opening Edit on the analyzed product (same typeId)
  // left the Brand dropdown showing just the OCR hits. Same per-concern
  // separation rationale as the split loading flags above.
  ocrProductModels: ProductModelSearchResult[];
  ocrManufacturers: ManufacturerSearchResult[];
}

export const initialState: InventoryState = {
  productGroups: [],
  products: [],
  product_count: null,
  inventory: [],
  product_id: null,
  product: null,
  productTypeGroupsList: [],
  productTemplate: null,
  searchedProducts: [],
  loading: false,
  productGroupLoading: false,
  productDetailsLoading: false,
  error: null,
  addItemModalVisible: false,
  addItemShowLanding: true,
  itemDetailsModalVisible: false,
  selectionDetailsModalVisible: false,
  selectionDetailsType: null,
  updateProductLoading: false,
  deleteProductLoading: false,
  productModels: [],
  productModelsLoading: false,
  manufacturers: [],
  manufacturersLoading: false,
  ocrProductModels: [],
  ocrManufacturers: []
};

export const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setInventoryLoading: (state, action: PayloadAction<any>) => {
      state.loading = action.payload;
    },
    fetchProductGroupCountSuccess: (state, action: PayloadAction<any>) => {
      const { items } = action.payload;
      state.productGroups = items;
    },
    // No loading flag to reset here — this action never sets one (see
    // fetchProductGroupError's comment for the sibling bug that DOES stem
    // from a stuck loading flag). Without this, a failed request only
    // surfaced a toast; `state.error` stayed unset for this fetch specifically.
    fetchProductGroupCountError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    fetchProductGroupStart: (state, action: PayloadAction<InventoryItem[]>) => {
      state.productGroupLoading = true;
    },
    fetchProductGroupSuccess: (
      state,
      action: PayloadAction<InventoryItem[]>
    ) => {
      state.products = action.payload;
      state.productGroupLoading = false;
    },
    // Was missing entirely — `requestProductGroup` had `onStart` (sets
    // `productGroupLoading = true`) but no `onError`, so any failed request
    // (network blip, timeout, mid-session 401) left it stuck `true` forever.
    fetchProductGroupError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.productGroupLoading = false;
    },
    resetProductGroup: (state) => {
      state.products = [];
      state.productGroupLoading = true;
    },
    fetchProductDetailsStart: (state) => {
      state.productDetailsLoading = true;
    },
    fetchProductDetailsSuccess: (state, action: PayloadAction<any>) => {
      state.product = action.payload;
      state.productDetailsLoading = false;
    },
    fetchProductTypeGroupsListSuccess: (state, action: PayloadAction<any>) => {
      state.productTypeGroupsList = action.payload;
    },
    fetchProductTypeGroupsListError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    fetchProductTemplateStart: (state) => {
      state.loading = true;
    },
    fetchProductTemplateSuccess: (state, action: PayloadAction<any>) => {
      state.productTemplate = action.payload;
      state.loading = false;
    },
    fetchProductTemplateError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    fetchSearchedProductsStart: (state) => {
      state.loading = true;
    },
    fetchSearchedProductsSuccess: (state, action: PayloadAction<any>) => {
      state.searchedProducts = action.payload;
      state.loading = false;
    },
    fetchSearchedProductsError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetSearchedProducts: (state) => {
      state.searchedProducts = [];
    },
    createProductStart: (state) => {
      state.loading = true;
    },
    createProductSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
    },
    createProductError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.loading = false;
    },
    showAddItemModal: (state) => {
      state.addItemModalVisible = true;
      state.addItemShowLanding = true;
    },
    hideAddItemModal: (state) => {
      state.addItemModalVisible = false;
      state.addItemShowLanding = true;
    },
    setAddItemShowLanding: (state, action: PayloadAction<boolean>) => {
      state.addItemShowLanding = action.payload;
    },
    showItemDetailsModal: (state, action: PayloadAction<any>) => {
      state.product_id = Number(action.payload);
      state.itemDetailsModalVisible = true;
    },
    hideItemDetailsModal: (state) => {
      state.product_id = null;
      state.product = null;
      state.itemDetailsModalVisible = false;
    },
    showSelectionDetailsModal: (
      state,
      action: PayloadAction<{ productId: number; type: 'option' | 'color' }>
    ) => {
      state.product_id = action.payload.productId;
      state.selectionDetailsType = action.payload.type;
      state.selectionDetailsModalVisible = true;
    },
    hideSelectionDetailsModal: (state) => {
      state.product_id = null;
      state.product = null;
      state.selectionDetailsModalVisible = false;
      state.selectionDetailsType = null;
      state.productDetailsLoading = false;
    },
    showDesktopItemDetails: (state, action: PayloadAction<any>) => {
      state.product_id = Number(action.payload);
    },
    resetInventoryDetails: (state) => {
      state.product_id = null;
      state.product = null;
    },
    resetProductTemplate: (state) => {
      state.loading = false;
      state.productTemplate = null;
    },
    updateProductStart: (state) => {
      state.updateProductLoading = true;
      // Cleared on start so callers can tell whether *this* update failed by
      // reading `error` right after awaiting the dispatch — `createApiRequest`
      // swallows API errors and resolves normally, so the slice is the only
      // signal available. Without this reset a stale error from any earlier
      // action would read as a failure of this one. See saveProductUpdate.
      state.error = null;
    },
    updateProductSuccess: (state, action: PayloadAction<InventoryItem>) => {
      state.product = action.payload;
      state.updateProductLoading = false;
    },
    updateProductError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.updateProductLoading = false;
    },
    deleteProductStart: (state) => {
      state.deleteProductLoading = true;
    },
    deleteProductSuccess: (state) => {
      state.deleteProductLoading = false;
      state.product = null;
      state.product_id = null;
    },
    deleteProductError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.deleteProductLoading = false;
    },
    searchProductModelsStart: (state) => {
      state.productModelsLoading = true;
    },
    searchProductModelsSuccess: (
      state,
      // Response is a paginated envelope ({ items, page, per_page, pages,
      // total, count, has_next, has_prev }), not a bare array — mirrors
      // fetchProductGroupCountSuccess's `{ items }` unwrap convention.
      action: PayloadAction<{ items: ProductModelSearchResult[] }>
    ) => {
      state.productModels = action.payload?.items || [];
      state.productModelsLoading = false;
    },
    searchProductModelsError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.productModelsLoading = false;
    },
    resetProductModels: (state) => {
      state.productModels = [];
    },
    searchManufacturersStart: (state) => {
      state.manufacturersLoading = true;
    },
    searchManufacturersSuccess: (
      state,
      action: PayloadAction<{ items: ManufacturerSearchResult[] }>
    ) => {
      state.manufacturers = action.payload?.items || [];
      state.manufacturersLoading = false;
    },
    searchManufacturersError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
      state.manufacturersLoading = false;
    },
    resetManufacturers: (state) => {
      state.manufacturers = [];
    },
    // OCR-only counterparts of the two search reducers above. They reuse the
    // shared `productModelsLoading`/`manufacturersLoading` flags deliberately:
    // the OCR lookups are awaited inline inside a modal that already shows its
    // own analyzing spinner, so nothing reads these flags for the OCR path —
    // what mattered was keeping the *results* separate.
    searchOcrProductModelsSuccess: (
      state,
      action: PayloadAction<{ items: ProductModelSearchResult[] }>
    ) => {
      state.ocrProductModels = action.payload?.items || [];
    },
    searchOcrManufacturersSuccess: (
      state,
      action: PayloadAction<{ items: ManufacturerSearchResult[] }>
    ) => {
      state.ocrManufacturers = action.payload?.items || [];
    },
    resetInventoryList: () => initialState
  }
});

export const {
  setInventoryLoading,
  fetchProductGroupCountSuccess,
  fetchProductGroupCountError,
  fetchProductGroupStart,
  fetchProductGroupSuccess,
  fetchProductGroupError,
  resetProductGroup,
  fetchProductTypeGroupsListSuccess,
  fetchProductTypeGroupsListError,
  fetchProductTemplateStart,
  fetchProductTemplateSuccess,
  fetchProductTemplateError,
  createProductStart,
  createProductSuccess,
  createProductError,
  fetchProductDetailsStart,
  fetchProductDetailsSuccess,
  fetchSearchedProductsStart,
  fetchSearchedProductsSuccess,
  fetchSearchedProductsError,
  resetSearchedProducts,
  resetProductTemplate,
  showAddItemModal,
  hideAddItemModal,
  setAddItemShowLanding,
  showItemDetailsModal,
  hideItemDetailsModal,
  showSelectionDetailsModal,
  hideSelectionDetailsModal,
  showDesktopItemDetails,
  resetInventoryDetails,
  updateProductStart,
  updateProductSuccess,
  updateProductError,
  deleteProductStart,
  deleteProductSuccess,
  deleteProductError,
  searchProductModelsStart,
  searchProductModelsSuccess,
  searchProductModelsError,
  resetProductModels,
  searchManufacturersStart,
  searchManufacturersSuccess,
  searchManufacturersError,
  resetManufacturers,
  searchOcrProductModelsSuccess,
  searchOcrManufacturersSuccess,
  resetInventoryList
} = inventorySlice.actions;

export default inventorySlice.reducer;
